import { useCallback, useEffect, MouseEvent } from 'react';

import { useToaster } from '../../toast';
import { useIsDragStarted } from '../../../hooks';
import { IUploadStore } from '../../../stores/uploadStore';
import { FileDto } from '@skillarea/lib-shared';
import { StoreApi, useStore } from 'zustand/index';

type TUploadDataType<C> = ReturnType<IUploadStore<C>['useFileData']>;

export function useFileLoader<C>(
  uploadStore: StoreApi<IUploadStore<C>>,
  slot: string,
  onChange?: (value: FileDto) => void,
) {
  const { toast } = useToaster();
  const fileData: TUploadDataType<C> = useStore(
    uploadStore,
    (state) => state.useFileData(slot) || undefined,
  );
  const { error, isLoading, name, url } = fileData || {};

  useEffect(() => {
    uploadStore.getState().clearSlot(slot);
  }, []);

  const isDragStarted = useIsDragStarted();
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    await uploadStore.getState().uploadFile(slot, acceptedFiles[0]);
  }, []);

  useEffect(() => {
    if (url) {
      onChange?.({ name: name ?? '', url });
      uploadStore.getState().clearSlot(slot);
    }
  }, [url]);

  useEffect(() => {
    if (name) {
      onChange?.({ name: name, url: url ?? '' });
    }
  }, [name]);

  useEffect(() => {
    if (error) {
      onChange?.({ name: '', url: '' });
      if (error !== 'signal is aborted without reason') toast.error(error);
    }
  }, [error]);

  const clearFile = useCallback((e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    uploadStore.getState().cancelFile(slot);
    onChange?.({ name: '', url: '' });
  }, []);

  const getFullUrl = useCallback(
    (file?: FileDto) => uploadStore.getState().getFullUrl(file),
    [],
  );

  return {
    getFullUrl,
    isLoading,
    name,
    url,
    isDragStarted,
    clearFile,
    onDrop,
    error,
  };
}
